'use client';
import { Html } from '@react-three/drei';
import { type Size, type ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import React, { useCallback, useEffect, useRef, useState } from 'react'; // @ts-ignore
import { type Shape, Vector3 } from 'three';
import { PropertyLegend } from '@/components/property-selector/draw-components/property-legend';
import {
  findMiddle,
  getNormalizedShapeVector,
  normalizePoint,
} from '@/components/property-selector/utils';
import { type Point, type PatchedPropertyUnit } from '@/types';

export function DemarcationShapeMaterial({
  color,
  opacity = 0.3,
}: {
  color: string | number | null;
  opacity?: number;
}) {
  if (!color) {
    return null;
  }
  return <meshBasicMaterial color={color} opacity={opacity} transparent={true} />;
}

type RenderDemarcationProps = {
  selected: boolean;
  property: PatchedPropertyUnit;
  shape: Shape;
  size: Size;
  showLegend: boolean;
  colorsSettings: string;
  hoverColor: string;
  availableColor: string;
  soldColor: string;
  onPointerOver?: (event: any, s: Size) => void;
  onPointerOut?: (event: any, s: Size) => void;
  onDemarcationClick?: (property: PatchedPropertyUnit) => void;
};

const tooltipHeight = 238;
const tooltipWidth = 240;
const selectedTooltipHeight = 278;
const tooltipPosition = new Vector3(0, 0, 0);

const style = { pointerEvents: 'none', minWidth: 200, display: 'flex' };
export function DrawDemarcation({
  property,
  shape,
  size,
  onPointerOver,
  onPointerOut,
  onDemarcationClick,
  selected,
  showLegend,
  colorsSettings,
  hoverColor,
  availableColor,
  soldColor,
}: RenderDemarcationProps) {
  const { camera, viewport } = useThree();
  const tooltipRef = useRef<any>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const dimensions = useRef<any>(null);
  const scroll = useRef<number>(0);

  if (!selected) {
    scroll.current = window.scrollY;
  }

  useEffect(() => {
    const _scroll = scroll.current;
    if (!selected) {
      dimensions.current = null;
    } else {
      dimensions.current = {
        width: viewport.width,
        height: viewport.height,
      };
      window.scrollTo(0, scroll.current);
    }

    return () => {
      window.scrollTo(0, _scroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useFrame(() => {
    if (!tooltipRef.current) return;
    let x = 0,
      y = 0;

    // This makes the initial tooltip position to be set to where the tooltip was last shown
    if (
      tooltipPosition.x !== 0 &&
      tooltipPosition.y !== 0 &&
      tooltipRef.current.position.x === 0 &&
      tooltipRef.current.position.y === 0
    ) {
      tooltipRef.current.position.set(tooltipPosition.x, tooltipPosition.y, tooltipPosition.z);
    }

    if (!selected && hovered) {
      const middle = normalizePoint(findMiddle(property.demarcationShape ?? []), size);

      const offsetY = tooltipHeight;
      const offsetX = tooltipWidth;
      const padding = 10;

      if (middle.x < 0) {
        const diff = Math.abs(middle.x - offsetX) - size.width / 2;

        x = diff > 0 ? middle.x + diff + padding : middle.x;
      } else {
        const diff = middle.x + offsetX - size.width / 2;
        x = diff > 0 ? middle.x - diff - padding : middle.x;
      }

      if (middle.y < 0) {
        const diff = Math.abs(middle.y - offsetY) - size.height / 2;
        y = diff > 0 ? middle.y + diff + padding : middle.y;
      } else {
        const diff = middle.y + offsetY - size.height / 2;
        y = diff > 0 ? middle.y - diff - padding : middle.y;
      }
    } else if (selected && dimensions.current) {
      const padding = 32;
      x = camera.position.x + (dimensions.current.width / 2 - tooltipWidth - padding) / camera.zoom;
      y = camera.position.y + (dimensions.current.height / 2 - padding) / camera.zoom;
    }
    tooltipPosition.set(x, y, 0);
    tooltipRef.current.position.lerp(tooltipPosition, 0.05);
  });

  const SOLD_OUT = property.state === 'sold';
  const DEFAULT_COLOR = SOLD_OUT ? soldColor : availableColor;

  function getMeshColor() {
    switch (colorsSettings) {
      case 'onlyShowDefaultColor':
        return hovered ? hoverColor : null;
      case 'showColorsAlways':
        return DEFAULT_COLOR;
      case 'showAvailabilityOnHover':
        return hovered ? DEFAULT_COLOR : null;
      default:
        return null;
    }
  }

  const onPointerOverCb = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!onPointerOver) return;
      onPointerOver(event, size);
      setHovered(true);
      event.stopPropagation();
    },
    [onPointerOver, size],
  );

  const onPointerOutCb = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (tooltipRef.current) {
        tooltipPosition.set(tooltipRef.current.position.x, tooltipRef.current.position.y, 0);
      }
      onPointerOut?.(event, size);
      setHovered(false);
      event.stopPropagation();
    },
    [onPointerOut, size],
  );

  const onClick = useCallback(() => {
    onDemarcationClick?.(property);
  }, [onDemarcationClick, property]);

  return (
    <group>
      <mesh onPointerOver={onPointerOverCb} onPointerOut={onPointerOutCb} onClick={onClick}>
        <DemarcationShapeMaterial color={getMeshColor()} />
        <shapeGeometry args={[shape]} />
      </mesh>
      {showLegend && (selected || hovered) && (
        <mesh ref={tooltipRef}>
          <Html position={[0, 0, 0]} style={style}>
            <PropertyLegend
              property={property}
              height={selected ? selectedTooltipHeight : tooltipHeight}
              width={tooltipWidth}
              selected={selected}
              availableColor={availableColor}
              soldColor={soldColor}
            />
          </Html>
        </mesh>
      )}
    </group>
  );
}

type TempDemarcationProps = {
  shape: Point[];
  size: Size;
  onPointerOver?: (event: any, s: Size) => void;
  onPointerOut?: (event: any, s: Size) => void;
};

export function TempDemarcation({
  shape,
  size,
  onPointerOver,
  onPointerOut,
}: TempDemarcationProps) {
  const [hovered, setHovered] = useState<boolean>(false);
  function getMeshColor() {
    if (hovered) {
      return '#028dff';
    }
    return '#02c4ff';
  }

  return (
    <mesh
      onPointerOver={(event) => {
        onPointerOver?.(event, size);
        setHovered(true);
      }}
      onPointerOut={(event) => {
        onPointerOut?.(event, size);
        setHovered(false);
      }}
    >
      <shapeGeometry args={[getNormalizedShapeVector(shape, size)]} />

      <DemarcationShapeMaterial color={getMeshColor()} />
    </mesh>
  );
}
